import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import 'dayjs/locale/pt-br';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Box } from '@mui/material';
import ColorPalette from '../../../../../colorpalette/ColorPalette';

dayjs.extend(isBetweenPlugin);
dayjs.locale('pt-br');

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop != 'isSelected' && prop != 'isHovered' && prop != 'isWeekView',
})(({ theme, isSelected, isHovered, day, isWeekView }) => ({
  borderRadius: isWeekView ? 0 : '50%',
  margin: '0px',
  padding: '0px',
  fontSize: '0.7rem',
  fontWeight: 400,
  color: '#374151',
  '&.MuiPickersDay-dayOutsideMonth': {
    color: '#C8D0DA',
  },
  ...(isSelected && !isWeekView && {
    backgroundColor: ColorPalette.secondary,
    color: '#FFFFFF',
    fontWeight: 600,
    '&:hover, &:focus': {
      backgroundColor: ColorPalette.secondaryDark,
    },
  }),
  ...(isSelected && isWeekView && {
    backgroundColor: ColorPalette.secondaryLight,
    color: ColorPalette.secondary,
    '&:hover, &:focus': {
      backgroundColor: ColorPalette.secondaryLight,
    },
  }),
  ...(isHovered && isWeekView && {
    backgroundColor: ColorPalette.secondaryLight,
    color: ColorPalette.secondary,
    '&:hover, &:focus': {
      backgroundColor: ColorPalette.secondaryLight,
    },
  }),
  ...(day.day() == 0 && isWeekView && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.day() == 6 && isWeekView && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
}));

const isInSameWeek = (dayA, dayB) => {
  if (dayB == null) {
    return false;
  }
  return dayA.isSame(dayB, 'week');
};

function Day(props) {
  const { day, selectedDay, hoveredDay, isWeekView, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      disableMargin
      selected={false}
      isSelected={isWeekView ? isInSameWeek(day, selectedDay) : day.isSame(selectedDay, 'day')}
      isHovered={isWeekView && isInSameWeek(day, hoveredDay)}
      isWeekView={isWeekView}
    />
  );
}

const CalendarDatePicker = ({ currentDate, viewDaysCount, onChange }) => {
  const [hoveredDay, setHoveredDay] = useState(null);
  const [value, setValue] = useState(dayjs(currentDate));
  const isWeekView = viewDaysCount > 1;

  const handleDateChange = (newValue) => {
    setValue(newValue);
    onChange(newValue.toDate());
  };

  useEffect(() => {
    setValue(dayjs(currentDate));
  }, [currentDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} locale="pt-br">
      <Box sx={{
        width: '100%',
        '& .MuiDateCalendar-root': {
          width: '100%',
          maxHeight: '260px',
          padding: '0 4px',
          margin: '0',
          '& .MuiYearCalendar-root, & .MuiMonthCalendar-root': {
            width: 'fit-content'
          }
        },
        '& .MuiMonthCalendar-root, & .MuiYearCalendar-root': {
          maxHeight: '210px',
          width: 'fit-content',
          margin: '0',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, auto)',
          columnGap: '8px',
          rowGap: '2px',
          padding: '4px',
          '& .MuiPickersMonth-monthButton, & .MuiPickersYear-yearButton': {
            fontSize: '0.7rem',
            padding: '0px',
            margin: '0px',
            minHeight: '18px',
            width: '45px',
            maxWidth: '45px',
            lineHeight: '1',
            borderRadius: '4px',
            justifyContent: 'center'
          }
        },
        '& .MuiPickersCalendarHeader-root': {
          justifyContent: 'flex-start',
          paddingLeft: '0',
          paddingRight: '0',
          minHeight: '32px',
          marginBottom: '4px',
          position: 'relative',
          '& .MuiPickersCalendarHeader-label': {
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#1A2035',
            marginRight: 'auto',
            marginLeft: '0',
          },
          '& .MuiPickersArrowSwitcher-root': {
            position: 'absolute',
            right: '0',
            '& .MuiIconButton-root': {
              padding: '2px',
              '& svg': {
                fontSize: '1rem',
                color: '#6B7280',
              }
            }
          }
        },
        '& .MuiDayCalendar-header': {
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0px',
          margin: '0 0 2px 0',
          '& .MuiTypography-root': {
            fontSize: '0.65rem',
            fontWeight: 500,
            color: '#9CA3AF',
            textAlign: 'center',
            width: '100%',
          }
        },
        '& .MuiDayCalendar-weekContainer': {
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0px',
          margin: '1px 0',
        },
        '& .MuiPickersDay-root': {
          width: '100%',
          height: '26px',
          fontSize: '0.7rem',
          margin: '0',
          borderRadius: '50%',
        },
        '& .MuiPickersDay-today': {
          border: 'none',
          backgroundColor: ColorPalette.secondary,
          color: '#FFFFFF',
          fontWeight: 600,
          '&:not(.Mui-selected)': {
            border: 'none',
            backgroundColor: ColorPalette.secondary,
            color: '#FFFFFF',
          }
        },
      }}>
        <DateCalendar
          value={value}
          onChange={handleDateChange}
          showDaysOutsideCurrentMonth
          slots={{ day: Day }}
          slotProps={{
            day: (ownerState) => ({
              selectedDay: value,
              hoveredDay,
              onPointerEnter: () => setHoveredDay(ownerState.day),
              onPointerLeave: () => setHoveredDay(null),
              isWeekView,
            }),
          }}
          dayOfWeekFormatter={(day) => {
            const letters = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
            return letters[day];
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CalendarDatePicker;
